import { Button } from '@/components/shadcn/button'
import { cn } from '@/lib/utils'
import { IconDragHandleOutline } from '@icons/IconDragHandleOutline'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { IconRightChevronOutline } from '@icons/IconRightChevronOutline'
import { IconV2TrashOutline } from '@icons/IconV2TrashOutline'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import clsx from 'clsx'
import {
  ActiveDropTarget,
  canAddContentToIndexNode,
  DropPosition,
  ReorderableIndexNode,
} from './indexReorderUtils'

function DropLine({ position }: { position: 'top' | 'bottom' }) {
  return (
    <div
      className={clsx(
        'pointer-events-none absolute inset-x-0 z-10 h-0.5 bg-primary',
        position === 'top' ? 'top-0' : 'bottom-0'
      )}
    />
  )
}

function ReorderableIndexRow({
  node,
  id,
  level,
  isExpanded,
  activeDropPosition,
  onToggleExpand,
  onDelete,
  onAddContent,
  canAddContent,
}: {
  node: ReorderableIndexNode
  id: string
  level: number
  isExpanded: boolean
  activeDropPosition?: DropPosition
  onToggleExpand: (id: string) => void
  onDelete: (id: string) => void
  onAddContent?: (id: string) => void
  canAddContent?: boolean
}) {
  const hasChildren = node.children.length > 0
  const showAddContent = canAddContent && onAddContent && canAddContentToIndexNode(node)
  const isWeightSemibold = level === 1
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id })
  const rowDrop = useDroppable({ id })

  const setRowRef = (element: HTMLDivElement | null) => {
    setNodeRef(element)
    rowDrop.setNodeRef(element)
  }

  return (
    <div
      ref={setRowRef}
      role="treeitem"
      aria-expanded={hasChildren ? isExpanded : undefined}
      style={{ opacity: isDragging ? 0.5 : undefined }}
      className={cn(
        'relative max-w-96 rounded text-foreground',
        activeDropPosition === 'inside' && 'bg-primary/10 ring-1 ring-inset ring-primary'
      )}
    >
      {activeDropPosition === 'before' && <DropLine position="top" />}
      {activeDropPosition === 'after' && <DropLine position="bottom" />}
      <div className="flex w-full gap-2 py-1.5 pr-3">
        <div className="flex min-w-0 flex-1 min-h-[1.375rem] items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6 shrink-0 cursor-grab bg-muted active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <IconDragHandleOutline className="size-4 text-muted-foreground" />
            <span className="sr-only">Drag index item</span>
          </Button>
          {hasChildren ? (
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-1 text-left"
              onClick={() => onToggleExpand(id)}
            >
              <IconRightChevronOutline
                className={cn('size-3 shrink-0 transition-transform duration-200', {
                  'rotate-90': isExpanded,
                })}
              />
              <span
                className={cn('min-w-0 whitespace-normal leading-snug', isWeightSemibold ? 'font-semibold' : '')}
              >
                {node.title}
              </span>
            </button>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-1">
              <span className="size-3 shrink-0" aria-hidden />
              <span
                className={cn('min-w-0 whitespace-normal leading-snug', isWeightSemibold ? 'font-semibold' : '')}
              >
                {node.title}
              </span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-start gap-0.5 self-start">
          {showAddContent ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-6 shrink-0"
              onClick={() => onAddContent(id)}
            >
              <IconPlusOutline className="size-4 text-muted-foreground" />
              <span className="sr-only">Add content</span>
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6 shrink-0"
            onClick={() => onDelete(id)}
          >
            <IconV2TrashOutline className="size-4 text-muted-foreground" />
            <span className="sr-only">Delete index item</span>
          </Button>
        </div>
      </div>
      {activeDropPosition === 'inside' && (
        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-primary">
          Drop inside
        </div>
      )}
    </div>
  )
}

function renderReorderRows(
  nodes: ReorderableIndexNode[],
  expandedItems: string[],
  activeDropTarget: ActiveDropTarget,
  onToggleExpand: (id: string) => void,
  onDelete: (id: string) => void,
  onAddContent?: (id: string) => void,
  canAddContent?: boolean,
  parentId = '',
  level = 1
) {
  const rows: JSX.Element[] = []

  nodes.forEach((node, index) => {
    const id = parentId ? `${parentId}-${index}` : `${index}`
    const isExpanded = expandedItems.includes(id)
    const hasChildren = node.children.length > 0

    rows.push(
      <ReorderableIndexRow
        key={id}
        node={node}
        id={id}
        level={level}
        isExpanded={isExpanded}
        activeDropPosition={activeDropTarget?.id === id ? activeDropTarget.position : undefined}
        onToggleExpand={onToggleExpand}
        onDelete={onDelete}
        onAddContent={onAddContent}
        canAddContent={canAddContent}
      />
    )

    if (isExpanded && hasChildren) {
      rows.push(
        <div key={`${id}-children`} role="group" className="flex flex-col gap-y-0.5 pl-4 pt-0.5">
          {renderReorderRows(
            node.children,
            expandedItems,
            activeDropTarget,
            onToggleExpand,
            onDelete,
            onAddContent,
            canAddContent,
            id,
            level + 1
          )}
        </div>
      )
    }
  })

  return rows
}

export function IndexReorderSelector({
  indexData,
  expandedItems,
  activeDropTarget,
  onToggleExpand,
  onDelete,
  onAddContent,
  canAddContent,
}: {
  indexData: ReorderableIndexNode[]
  expandedItems: string[]
  activeDropTarget: ActiveDropTarget
  onToggleExpand: (id: string) => void
  onDelete: (id: string) => void
  onAddContent?: (id: string) => void
  canAddContent?: boolean
}) {
  return (
    <div className="relative flex max-w-96 flex-col gap-y-1" role="tree" aria-label="Index Hierarchy">
      {renderReorderRows(
        indexData,
        expandedItems,
        activeDropTarget,
        onToggleExpand,
        onDelete,
        onAddContent,
        canAddContent
      )}
    </div>
  )
}

export function IndexReorderDragOverlay({ node }: { node: ReorderableIndexNode }) {
  return (
    <div className="flex max-w-96 items-center gap-2 rounded-md bg-primary/10 px-3 py-2 text-sm shadow-md">
      <IconDragHandleOutline className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 truncate font-medium">{node.title}</span>
    </div>
  )
}
